import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import SlideInModal from 'components/Modal/SlideInModal'
import UserHistorySkeleton from 'components/global/CUK/Skeletons/UserHistorySkeleton'
import ViewModeToggle from './ViewModeToggle'
import HistoryItem from './HistoryItem'
import EmptyHistoryState from './EmptyHistoryState'
import { UserHistoryModalProps, ViewMode } from '../types'
import { useHistoryQuery } from '../hooks/useHistoryQuery'
import { useExpandableItems } from '../hooks/useExpandableItems'

const UserHistoryModal: React.FC<UserHistoryModalProps> = ({
  userId,
  userName,
  onClose,
  isInternal,
  sfContactId,
  email
}) => {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<ViewMode>('by_resource')
  const { expandedItems, toggleExpand, initializeExpandedState } = useExpandableItems()

  const { loading, error, data } = useHistoryQuery({
    userId,
    sfContactId,
    email,
    isInternal,
    viewMode,
    onDataLoaded: initializeExpandedState
  })

  const handleViewModeToggle = (newMode: ViewMode) => {
    if (newMode !== viewMode) {
      setViewMode(newMode)
    }
  }

  const renderContent = () => {
    if (loading) {
      return <UserHistorySkeleton />
    }

    if (error) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="error" sx={{ fontSize: 10 }}>
            {t('auth.history.error', 'Erreur lors du chargement de l\'historique')}
          </Typography>
        </Box>
      )
    }

    if (!data || data.length === 0) {
      return <EmptyHistoryState />
    }

    return data.map((item) => (
      <HistoryItem
        key={item.id}
        item={item}
        isExpanded={!!expandedItems[item.id]}
        viewMode={viewMode}
        onToggle={() => toggleExpand(item.id)}
      />
    ))
  }

  return (
    <SlideInModal
      onClose={onClose}
      icon={<i className="fa-solid fa-list-timeline" />}
      title={
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 400 }}>
            {t('auth.history.title', 'Historique')}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {userName ? `${userName}` : ''}
          </Typography>
        </Box>
      }
    >
      <ViewModeToggle viewMode={viewMode} onChange={handleViewModeToggle} />
      {renderContent()}
    </SlideInModal>
  )
}

export default UserHistoryModal
